import { Breadcrumb } from "@/components/breadcrumb";
import { PageHeader } from "@/components/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/lib/auth-client";
import { useFeatureFlag } from "@/lib/hooks/use-admin";
import { useSsoProvider } from "@/lib/hooks/use-sso";
import { useT } from "@/lib/i18n/use-t";
import { hasPermissionType, type Permission } from "@/lib/permissions";
import { T } from "./page.i18n";
import { SsoSettings } from "./sso-settings";

export default function SsoSettingsPage() {
  const t = useT(T);
  const { data: sessionData } = useSession();
  const { settings, provider, isLoading: providerLoading } = useSsoProvider();
  const { enabled: hasSsoFlag, isLoading: flagLoading } = useFeatureFlag("sso");

  const userPermissions = ((sessionData?.user as any)?.permissions || ["advisor"]) as Permission[];
  const canManageSso =
    hasPermissionType(userPermissions, "admin") || hasPermissionType(userPermissions, "developer");

  if (flagLoading || providerLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={[{ label: t.settings, href: "/settings" }, { label: t.singleSignOn }]} />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canManageSso) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={[{ label: t.settings, href: "/settings" }, { label: t.singleSignOn }]} />
        <Alert>
          <AlertTitle>{t.featureNotEnabled}</AlertTitle>
          <AlertDescription>{t.noPermission}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!hasSsoFlag) {
    return (
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.singleSignOn }]}
          title={t.singleSignOn}
          description={t.pageDescription}
        />
        <Alert>
          <AlertTitle>{t.featureNotEnabled}</AlertTitle>
          <AlertDescription>{t.featureNotEnabledDescription}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.singleSignOn }]}
        title={t.singleSignOn}
        description={t.pageDescription}
      />
      <SsoSettings
        ssoEnabled={!!provider}
        isConfigured={!!provider}
        providerId={settings?.providerId}
        callbackUrl={settings?.callbackUrl}
        pendingChange={settings?.pendingChange ?? null}
        oidcIssuer={provider?.issuer}
        oidcClientId={provider?.clientId}
        oidcName={provider?.name}
        hasClientSecret={provider?.hasClientSecret ?? false}
      />
    </div>
  );
}
